import { useMemo, useEffect, useRef, useState, startTransition } from 'react';
import { useEventStore } from '@/state/stores/use-event-store';
import { useModeStore } from '@/state/stores/use-mode-store';
import { getObjectId } from '@/helpers/object.id.helper';
import type { Mode } from '@/types/entity-mode';
import Widget from '@/components/common/widget';
import { EventModeSection } from '@/components/events/detail/event-mode-section';
import { useTranslations } from '@/i18n/use-translations';
import { alpha } from '@mui/material/styles';

export default function EventMode() {
  const t = useTranslations();
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventOrganize = useEventStore((s) => s.eventOrganize);

  // Get event origin ID for storing modes
  const eventOriginId = useMemo(() => {
    if (!event) return null;
    return event.origin ? getObjectId(event.origin) : event.id;
  }, [event]);

  const setMode = useModeStore((s) => s.setMode);
  const storedMode = useModeStore((s) => (eventOriginId ? s.eventMode[eventOriginId] : undefined));

  const computedMode = useMemo(() => {
    if (storedMode === 'PLAN' && eventPlan) return storedMode;
    if ((storedMode === 'ORGANIZE' || storedMode === 'PERFORM') && eventOrganize) return storedMode;
    return eventPlan ? 'PLAN' : eventOrganize ? 'ORGANIZE' : (event?.mode ?? 'PLAN');
  }, [storedMode, eventPlan, eventOrganize, event?.mode]);

  const [currentMode, setCurrentMode] = useState<Mode>(computedMode);
  const previousComputedModeRef = useRef<Mode>(computedMode);
  const userInitiatedChangeRef = useRef(false);

  // Sync computedMode to currentMode when underlying data or stored mode changes
  useEffect(() => {
    // If user manually changed mode and stored mode matches, don't override
    if (userInitiatedChangeRef.current && computedMode === previousComputedModeRef.current) {
      return;
    }

    // Reset user-initiated flag if computed mode changed from external data
    if (computedMode !== previousComputedModeRef.current) {
      userInitiatedChangeRef.current = false;
      startTransition(() => {
        setCurrentMode(computedMode);
      });
    }

    previousComputedModeRef.current = computedMode;
  }, [computedMode]);

  const handleModeChange = (newMode: Mode) => {
    if (newMode === 'PLAN' && !eventPlan) return;
    if ((newMode === 'ORGANIZE' || newMode === 'PERFORM') && !eventOrganize) return;
    userInitiatedChangeRef.current = true;
    setCurrentMode(newMode);
    if (eventOriginId) {
      const eventMode = useModeStore.getState().eventMode;
      setMode({
        eventMode: {
          ...eventMode,
          [eventOriginId]: newMode,
        },
      });
    }
  };

  const hasPlanMode = !!eventPlan;
  const hasOrganizeMode = !!eventOrganize;

  if (!event) return null;

  const getBackgroundColor = (theme: { palette: { error: { main: string }; secondary: { main: string } } }) => {
    if (currentMode === 'PERFORM') {
      return alpha(theme.palette.error.main, 0.2);
    }
    if (currentMode === 'ORGANIZE') {
      return alpha(theme.palette.secondary.main, 0.2);
    }
    // PLAN mode: normal background (no override)
    return undefined;
  };

  const title = `${t('flows.mode', { defaultValue: 'BRITESTAGE Process' })}: ${currentMode}`;

  return (
    <Widget
      title={title}
      sx={{
        borderRadius: 0,
        bgcolor: getBackgroundColor,
      }}
    >
      <EventModeSection
        currentMode={currentMode}
        hasPlanMode={hasPlanMode}
        hasOrganizeMode={hasOrganizeMode}
        handleModeChange={handleModeChange}
        event={eventPlan ?? eventOrganize ?? event}
        eventSlotIdParam={null}
        showDivider={false}
      />
    </Widget>
  );
}
